/**
 * `/support` — the self-serve answers first, then your tickets.
 *
 * The order is the whole design of this page. Two of the three things people
 * write in about are already buttons elsewhere in this portal, and a customer
 * who opens a ticket to ask "how do I free up a device slot" waits hours for an
 * answer they could have had in ten seconds. So the answers come first and the
 * "open a ticket" control comes after them — not hidden, just second.
 *
 * That is a deliberate trade against making the ticket button the hero. It
 * costs a little friction for people who genuinely need a human, and it is
 * worth it: the ticket that is never filed is the one that is answered fastest,
 * and there is exactly one person answering these.
 *
 * ── Width ───────────────────────────────────────────────────────────────────
 *
 * This screen sets no width of its own, which is exactly what makes it the same
 * measure as the licenses list: `PortalShell` caps every page at 64rem, and
 * `PageHeader` already holds its own description to a readable line inside
 * that. All three support screens used to be pinned to `max-w-2xl` — the form
 * measure that `/signin`, `/claim` and `/account` want — and applying it to
 * what are actually rows of records left the support area as a narrow column
 * with the rest of the portal wide around it. What still gets a measure is
 * prose and things you type into, and those are constrained where they are
 * written rather than by squeezing the whole page.
 */

import { PageHeader } from "@/components/PageHeader";
import { Empty, Marker, Notice, Strip, StripHead, Tag, TearLine } from "@/components/deck";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { PortalError, portalApi, useApiQuery } from "@/lib/api";
import type { TicketSummary } from "@/lib/api";
import { isoDate, relative, stamp } from "@/lib/format";
import { ArrowRight, Mail, MessageSquarePlus } from "lucide-react";
import { Link } from "react-router";
import { needsReply } from "../../shared/support";

/** Kept in sync with `PORTAL_MAIL_REPLY_TO` in wrangler.jsonc. */
const SUPPORT_EMAIL = "support@downwind.studio";

export function SupportRoute() {
  const tickets = useApiQuery([], (signal) => portalApi.listTickets(signal), {
    suppressToast: true,
  });

  const list = tickets.data?.tickets ?? [];
  const unavailable = tickets.error instanceof PortalError && tickets.error.status === 503;

  return (
    <>
      <PageHeader
        code="SUP"
        label="Support"
        title={
          <>
            Get <Marker draw>help</Marker>
          </>
        }
        description="Downwind Studio is small. A ticket reaches a person, and usually the same day."
        actions={
          !unavailable ? (
            <Button asChild>
              <Link to="/support/new">
                <MessageSquarePlus aria-hidden="true" />
                Open a ticket
              </Link>
            </Button>
          ) : null
        }
      />

      <section aria-labelledby="self-serve-head">
        <Strip bar={false}>
          <StripHead
            title="Fix it yourself, faster"
            as="h2"
            id="self-serve-head"
            note="Two of the three things people write in about are buttons in here."
          />
          <div>
            <Answer
              question="I have a new PC and it says I am out of devices."
              answer="Open the license, find the machine you no longer use by its last-seen date, and release it. The slot frees up immediately — there is no waiting period."
              to="/"
              cta="Your licenses"
            />
            <Answer
              question="I have lost my license key."
              answer="Open the license and press Show key. It is the same key that was in your original email; reading it here does not change or replace it."
              to="/"
              cta="Your licenses"
            />
            <Answer
              question="I bought it somewhere else and nothing is here."
              answer="Marketplaces hand over a key without telling us who bought it. Add the key and the license joins this account permanently."
              to="/claim"
              cta="Add a key"
            />
          </div>
        </Strip>
      </section>

      <TearLine className="my-8" />

      <section aria-labelledby="tickets-head">
        <Strip bar={false}>
          <StripHead title="Your tickets" as="h2" id="tickets-head">
            {list.length > 0 ? (
              <Button asChild variant="outline" size="sm">
                <Link to="/support/new">Open another</Link>
              </Button>
            ) : null}
          </StripHead>

          {tickets.isLoading ? (
            <div className="px-4 py-5">
              <Skeleton className="h-4 w-2/3" />
              <Skeleton className="mt-3 h-3 w-1/3" />
            </div>
          ) : unavailable ? (
            <SupportDown />
          ) : tickets.error ? (
            <Notice
              tone="hold"
              label="Could not load"
              className="m-4"
              action={
                <Button variant="outline" size="sm" onClick={tickets.refetch}>
                  Try again
                </Button>
              }
            >
              {tickets.error.message}
            </Notice>
          ) : list.length === 0 ? (
            <Empty>
              You have not opened any tickets. If none of the above sorted it out, open one and we
              will pick it up.
            </Empty>
          ) : (
            <div>
              {list.map((ticket) => (
                <TicketRow key={ticket.id} ticket={ticket} />
              ))}
            </div>
          )}
        </Strip>
      </section>

      <p className="mx-auto mt-6 max-w-2xl text-center text-[0.8125rem] leading-relaxed text-ink-2">
        You can also email{" "}
        <a href={`mailto:${SUPPORT_EMAIL}`} className="text-ink underline underline-offset-4">
          {SUPPORT_EMAIL}
        </a>{" "}
        — though a ticket arrives with your licenses already attached, which usually saves a round
        trip.
      </p>
    </>
  );
}

/**
 * One ticket in the list.
 *
 * The status a customer reads is not the database's. `needsReply` is the shared
 * derivation both inboxes use, and it is what turns "open" into the two states
 * that actually differ to the person waiting: we owe you an answer, or you owe
 * us one. An "open" ticket the customer was last to speak on says *With us*,
 * which is the reassurance the page exists to give.
 */
function TicketRow({ ticket }: { ticket: TicketSummary }) {
  const waiting = needsReply(ticket);
  const resolved = ticket.status !== "open";

  return (
    <Link
      to={`/support/${ticket.id}`}
      className="group flex flex-wrap items-center gap-x-6 gap-y-2 border-b border-rule px-4 py-4 transition-colors last:border-b-0 hover:bg-marker/10 focus-visible:-outline-offset-2"
    >
      <div className="min-w-0 flex-1 basis-64">
        <div className="flex items-center gap-2">
          <span className="t-field shrink-0">#{ticket.number}</span>
          <span className="truncate text-[0.9375rem] text-ink">{ticket.subject}</span>
        </div>
        <div className="t-field mt-1.5">
          Opened {isoDate(ticket.created_at)} · last activity{" "}
          <span title={stamp(ticket.last_message_at)}>{relative(ticket.last_message_at)}</span>
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-3">
        {resolved ? (
          <Tag dot={false}>{ticket.status.toUpperCase()}</Tag>
        ) : waiting ? (
          <Tag tone="marker" dot={false}>
            WITH US
          </Tag>
        ) : (
          <Tag tone="cleared" dot={false}>
            YOUR MOVE
          </Tag>
        )}
        <ArrowRight
          aria-hidden="true"
          className="size-4 text-ink-3 transition-colors group-hover:text-ink"
        />
      </div>
    </Link>
  );
}

/**
 * Support is provisioned separately from the rest of the portal, so it can be
 * legitimately absent on a working deploy. When it is, this page still has to
 * be useful — hence the mailbox, which is what actually happens in that case.
 */
function SupportDown() {
  return (
    <Notice
      tone="marker"
      label="Tickets are not available"
      className="m-4"
      action={
        <Button asChild variant="outline" size="sm">
          <a href={`mailto:${SUPPORT_EMAIL}`}>
            <Mail aria-hidden="true" />
            Email us
          </a>
        </Button>
      }
    >
      We cannot reach the ticket system right now. Email {SUPPORT_EMAIL} — or just reply to your
      order confirmation — and it reaches the same person.
    </Notice>
  );
}

function Answer({
  question,
  answer,
  to,
  cta,
}: {
  question: string;
  answer: string;
  to: string;
  cta: string;
}) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-3 border-b border-rule px-4 py-4 last:border-b-0">
      <div className="min-w-0 flex-1 basis-72">
        <h3 className="text-[0.9375rem] font-medium leading-snug text-ink">{question}</h3>
        <p className="mt-1.5 text-[0.875rem] leading-relaxed text-ink-2">{answer}</p>
      </div>
      <Button asChild variant="outline" size="sm" className="shrink-0">
        <Link to={to}>{cta}</Link>
      </Button>
    </div>
  );
}
